// src/utils/deviceSettings.js
import config from "../config";
import logger from "./logger";

/**
 * Collects device settings by calling the backend API
 * The backend will execute the GraphQL queries against Yonomi Platform
 * @param {string} deviceId - Device ID to query
 * @returns {Promise<Object>} Combined device settings
 */
export async function collectDeviceSettings(deviceId) {
  logger.log(`[DeviceSettings] Starting collection for device ${deviceId}`);

  try {
    const response = await fetch(
      `${config.apiEndpoint}/device-settings?deviceId=${deviceId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": config.apiKey,
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to fetch device settings: ${response.status} - ${errorText}`
      );
    }

    const result = await response.json();
    logger.log("[DeviceSettings] Device settings collection complete");

    return result;
  } catch (error) {
    logger.error("[DeviceSettings] Failed to collect settings:", error);
    throw error;
  }
}

/**
 * Downloads device settings as JSON file
 * @param {Object} data - Device data to download
 * @param {string} deviceId - Device ID for filename
 */
export function downloadSettingsFile(data, deviceId) {
  // Create filename with timestamp
  const timestamp = new Date().toISOString().replace(/:/g, "-");
  const filename = `${deviceId}-${timestamp}.json`;

  // Create blob and download
  const jsonString = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonString], { type: "application/json" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up URL
  URL.revokeObjectURL(url);

  logger.log(`[DeviceSettings] Downloaded settings file: ${filename}`);
}
